import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { useBookingModal } from "../contexts/BookingModalContext";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const { openModal } = useBookingModal();

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isServicesActive = () => {
    return location.pathname.startsWith("/services");
  };

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/our-team", label: "Our Team" },
    { path: "/outreach", label: "Outreach" },
    { path: "/blog", label: "Blog" },
    { path: "/resources", label: "Resources" },
    { path: "/contact", label: "Contact" },
    { path: "/healing-within-reach", label: "Donate" },
  ];

  const servicesSubItems = [
    { path: "/services/drug-alcohol", label: "Drug and Alcohol Services" },
    { path: "/services/outpatient", label: "Outpatient Therapy Services" },
  ];

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-[9999] backdrop-blur-md transition-all duration-300 border-b border-white/10"
        style={{ backgroundColor: 'rgba(41, 48, 57, 0.85)' }}
      >
        <div className="h-20 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-full">
            <Link to="/" className="flex items-center space-x-3 flex-shrink-0">
              <img
                src="/images/Michelle Simmons Logo Resized.png"
                alt="Michelle Simmons Counseling Group"
                className="h-12 w-auto"
                style={{ borderRadius: '60px' }}
              />
            </Link>

            <nav className="hidden xl:flex items-center space-x-6 xl:space-x-10">
              {navItems.slice(0, 2).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative transition-all duration-300 ease-out group ${isActive(item.path)
                    ? "text-mscg-orange font-bold"
                    : "text-white hover:text-mscg-orange"
                    }`}
                  style={{ fontSize: "1rem" }}
                >
                  {item.label}
                  <span
                    className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-mscg-orange transition-all duration-300 ease-out group-hover:w-full ${isActive(item.path) ? "w-full" : ""
                      }`}
                  ></span>
                </Link>
              ))}

              {/* Services Dropdown */}
              <div
                className="relative pb-4 -mb-4"
                onMouseEnter={() => setServicesDropdownOpen(true)}
                onMouseLeave={() => setServicesDropdownOpen(false)}
              >
                <button
                  className={`relative transition-all duration-300 ease-out group flex items-center gap-1 ${isServicesActive()
                    ? "text-mscg-orange font-bold"
                    : "text-white hover:text-mscg-orange"
                    }`}
                  style={{ fontSize: "1rem" }}
                >
                  Services
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${servicesDropdownOpen ? "rotate-180" : ""
                      }`}
                  />
                  <span
                    className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-mscg-orange transition-all duration-300 ease-out group-hover:w-full ${isServicesActive() ? "w-full" : ""
                      }`}
                  ></span>
                </button>

                {/* Dropdown Menu */}
                <div
                  className={`absolute top-full left-0 pt-4 w-72 transition-all duration-300 origin-top ${servicesDropdownOpen
                    ? "opacity-100 scale-100 translate-y-0"
                    : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                    }`}
                >
                  <div className="bg-mscg-dark-gray rounded-xl shadow-2xl border border-white/10 overflow-hidden">
                    <div className="py-2">
                      {servicesSubItems.map((subItem, index) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`block px-5 py-3 transition-all duration-200 ${isActive(subItem.path)
                            ? "text-mscg-orange bg-white/5"
                            : "text-white/90 hover:text-mscg-orange hover:bg-white/5"
                            }`}
                          style={{
                            animation: servicesDropdownOpen
                              ? `slideIn 0.3s ease-out ${index * 0.05}s backwards`
                              : "none",
                          }}
                        >
                          <span className="font-medium text-[0.95rem]">{subItem.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {navItems.slice(2).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative transition-all duration-300 ease-out group ${isActive(item.path)
                    ? "text-mscg-orange font-bold"
                    : "text-white hover:text-mscg-orange"
                    }`}
                  style={{ fontSize: "1rem" }}
                >
                  {item.label}
                  <span
                    className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-mscg-orange transition-all duration-300 ease-out group-hover:w-full ${isActive(item.path) ? "w-full" : ""
                      }`}
                  ></span>
                </Link>
              ))}
            </nav>

            <div className="hidden xl:flex items-center space-x-4">
              <button
                onClick={openModal}
                className="inline-flex items-center gap-3 bg-white text-black pl-8 pr-2 py-2 rounded-full font-bold transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl group whitespace-nowrap"
                style={{ fontSize: "1rem" }}
              >
                Book Now
                <div className="w-10 h-10 rounded-full bg-[#d68800] text-white flex items-center justify-center transition-all duration-300 group-hover:bg-[#891719] group-hover:rotate-45">
                  <ArrowUpRight className="w-5 h-5" />
                </div>
              </button>
            </div>

            <div className="flex xl:hidden items-center gap-2">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border-2 border-white/20 bg-transparent hover:bg-white/10 transition-colors"
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5 text-white" />
                ) : (
                  <Menu className="h-5 w-5 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileMenuOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] xl:hidden">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="absolute right-0 top-0 h-full w-[85vw] max-w-sm bg-mscg-dark-gray shadow-2xl overflow-y-auto border-l border-white/10">
              <nav className="flex flex-col h-full p-6 pt-20">
                <div className="flex flex-col space-y-1 mb-8">
                  {navItems.slice(0, 2).map((item, index) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`text-lg transition-all duration-200 px-4 py-3 rounded-lg ${isActive(item.path)
                        ? "text-mscg-orange font-semibold bg-white/5"
                        : "text-white hover:bg-white/5"
                        }`}
                      style={{
                        fontWeight: isActive(item.path) ? 600 : 400,
                        animation: `slideIn 0.3s ease-out ${index * 0.05}s backwards`,
                      }}
                    >
                      {item.label}
                    </Link>
                  ))}

                  {/* Mobile Services Accordion */}
                  <div>
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className={`w-full text-lg transition-all duration-200 px-4 py-3 rounded-lg flex items-center justify-between ${isServicesActive()
                        ? "text-mscg-orange font-semibold bg-white/5"
                        : "text-white hover:bg-white/5"
                        }`}
                      style={{
                        animation: `slideIn 0.3s ease-out 0.1s backwards`,
                      }}
                    >
                      Services
                      <ChevronDown
                        className={`w-5 h-5 transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""
                          }`}
                      />
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${mobileServicesOpen ? "max-h-40" : "max-h-0"
                        }`}
                    >
                      <div className="pl-4 py-2 space-y-1">
                        {servicesSubItems.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`block text-base px-4 py-2 rounded-lg transition-all duration-200 ${isActive(subItem.path)
                              ? "text-mscg-orange bg-white/5"
                              : "text-white/80 hover:text-white hover:bg-white/5"
                              }`}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>

                  {navItems.slice(2).map((item, index) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`text-lg transition-all duration-200 px-4 py-3 rounded-lg ${isActive(item.path)
                        ? "text-mscg-orange font-semibold bg-white/5"
                        : "text-white hover:bg-white/5"
                        }`}
                      style={{
                        fontWeight: isActive(item.path) ? 600 : 400,
                        animation: `slideIn 0.3s ease-out ${(index + 3) * 0.05}s backwards`,
                      }}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    openModal();
                    setMobileMenuOpen(false);
                  }}
                  className="inline-flex items-center justify-center gap-3 bg-white text-black pl-8 pr-2 py-2 rounded-full font-bold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl group mb-6 w-full"
                  style={{ fontSize: "1rem" }}
                >
                  Book Now
                  <div className="w-10 h-10 rounded-full bg-[#d68800] text-white flex items-center justify-center transition-all duration-300 group-hover:bg-[#891719] group-hover:rotate-45">
                    <ArrowUpRight className="w-5 h-5" />
                  </div>
                </button>

                <div className="mt-auto pt-6 border-t border-white/10">
                  <p className="text-neutral-400 text-xs text-left">
                    © 2024 Michelle Simmons Counseling Group
                  </p>
                </div>

                <style>{`
                  @keyframes slideIn {
                    from {
                      opacity: 0;
                      transform: translateX(20px);
                    }
                    to {
                      opacity: 1;
                      transform: translateX(0);
                    }
                  }
                `}</style>
              </nav>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

